import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface DashboardLayoutState {
  showViewSwitcher: boolean;
  activeView: 'cards' | 'list' | 'table';
  onViewChange?: (view: 'cards' | 'list' | 'table') => void;
  forceSidebarCollapse: boolean;
}

interface DashboardLayoutContextType extends DashboardLayoutState {
  /** True when the persistent shell in App.tsx owns the layout */
  isShellActive: boolean;
  setLayoutState: (state: Partial<DashboardLayoutState>) => void;
  resetLayoutState: () => void;
}

const defaultState: DashboardLayoutState = {
  showViewSwitcher: false,
  activeView: 'cards',
  onViewChange: undefined,
  forceSidebarCollapse: false,
};

const DashboardLayoutContext = createContext<DashboardLayoutContextType>({
  ...defaultState,
  isShellActive: false,
  setLayoutState: () => {},
  resetLayoutState: () => {},
});

export function DashboardLayoutProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DashboardLayoutState>(defaultState);

  const setLayoutState = useCallback((patch: Partial<DashboardLayoutState>) => {
    setState(prev => ({ ...prev, ...patch }));
  }, []);

  const resetLayoutState = useCallback(() => {
    setState(defaultState);
  }, []);

  return (
    <DashboardLayoutContext.Provider value={{ ...state, isShellActive: true, setLayoutState, resetLayoutState }}>
      {children}
    </DashboardLayoutContext.Provider>
  );
}

export function useDashboardLayout() {
  return useContext(DashboardLayoutContext);
}

/**
 * Hook for pages to push their layout preferences to the persistent shell.
 * Resets to defaults on unmount.
 */
export function usePageLayout(options: Partial<DashboardLayoutState>) {
  const { isShellActive, setLayoutState, resetLayoutState } = useDashboardLayout();

  useEffect(() => {
    if (!isShellActive) return;
    setLayoutState(options);
    return () => resetLayoutState();
    // We deliberately omit options from deps — callers should pass stable values or use useMemo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShellActive]);
}



